import { useEffect, useState } from 'react'
import { Modal, Text, Stack, Group, Button, Alert } from '@mantine/core'

export interface LockConfirmModalProps {
  opened: boolean
  /** Human-readable description of what is about to change. */
  description: string
  /** Cooldown (ms) before Confirm enables. Default 3000. */
  cooldownMs?: number
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirmation modal shown before any routing change that touches on-air
 * content. A forced cooldown on the Confirm button prevents muscle-memory
 * acceptance of destructive broadcast changes.
 */
export function LockConfirmModal({
  opened,
  description,
  cooldownMs = 3_000,
  onConfirm,
  onCancel,
}: LockConfirmModalProps) {
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(cooldownMs / 1000))

  useEffect(() => {
    if (!opened) return
    setSecondsLeft(Math.ceil(cooldownMs / 1000))
    const start = Date.now()
    const id = setInterval(() => {
      const remaining = Math.max(0, cooldownMs - (Date.now() - start))
      setSecondsLeft(Math.ceil(remaining / 1000))
      if (remaining <= 0) clearInterval(id)
    }, 100)
    return () => clearInterval(id)
  }, [opened, cooldownMs])

  const confirmDisabled = secondsLeft > 0

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title="On-Air Content Warning"
      centered
      data-testid="lock-confirm-modal"
    >
      <Stack gap="sm">
        <Alert color="red" variant="light">
          This will affect on-air content. Confirm?
        </Alert>
        <Text size="sm">{description}</Text>
        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={onCancel} data-testid="lock-cancel-btn">Cancel</Button>
          <Button
            color="red"
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-testid="lock-confirm-btn"
          >
            {confirmDisabled ? `Confirm (${secondsLeft})` : 'Confirm'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
